import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useWhoami } from './auth/useAuthClient';

const SESSION_DURATION_SECONDS = 60 * 19;
const WARNING_THRESHOLD = 5;
const COUNTDOWN_INTERVAL = 1000;

let timeoutDelay = SESSION_DURATION_SECONDS;
let timeRemaining = SESSION_DURATION_SECONDS;
let lastDate = new Date();
let currentDate = new Date();

export const useSessionMonitor = () => {
  const { isAuthenticated, signOut } = useAuth();
  const whoami = useWhoami();
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const intervalRef = useRef(null);
  const whoamiRef = useRef(whoami);
  const signOutRef = useRef(signOut);
  const hasSignedOutRef = useRef(false);

  useEffect(() => {
    whoamiRef.current = whoami;
  }, [whoami]);

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRemainingSeconds(null);
      setShowWarning(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      timeoutDelay = SESSION_DURATION_SECONDS;
      timeRemaining = SESSION_DURATION_SECONDS;
      lastDate = new Date();
      hasSignedOutRef.current = false;
      return;
    }

    hasSignedOutRef.current = false;
    lastDate = new Date();
    timeoutDelay = SESSION_DURATION_SECONDS;
    timeRemaining = SESSION_DURATION_SECONDS;
    setRemainingSeconds(SESSION_DURATION_SECONDS);

    intervalRef.current = setInterval(() => {
      currentDate = new Date();
      timeRemaining = Math.round(
        timeoutDelay - (currentDate - lastDate) / 1000
      );
      if (timeRemaining < 0) {
        timeRemaining = 0;
      }

      setRemainingSeconds(timeRemaining);

      if (timeRemaining <= WARNING_THRESHOLD) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      if (timeRemaining === 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (!hasSignedOutRef.current) {
          hasSignedOutRef.current = true;
          // Keep modal visible for 2 seconds to show "Session Expired" message
          setTimeout(() => {
            setShowWarning(false);
            signOutRef.current();
          }, 2000);
        }
      }
    }, COUNTDOWN_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated]);

  const handleContinue = useCallback(async () => {
    try {
      await whoamiRef.current({}, {}, {});
      lastDate = new Date();
      timeoutDelay = SESSION_DURATION_SECONDS;
      timeRemaining = SESSION_DURATION_SECONDS;
      setRemainingSeconds(SESSION_DURATION_SECONDS);
      setShowWarning(false);
      hasSignedOutRef.current = false;
    } catch (error) {
      setShowWarning(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setShowWarning(false);
    if (timeRemaining <= 0 && !hasSignedOutRef.current) {
      hasSignedOutRef.current = true;
      signOutRef.current();
    }
  }, []);

  return {
    remainingSeconds,
    showWarning,
    handleContinue,
    handleDismiss,
  };
};
